import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { EMPTY, fromEvent, Observable, of } from "rxjs";
import { filter, switchMap, tap } from "rxjs/operators";
import { NeedaConfig } from "src/app/inventory/supplier/needa-config";
import { Invoice } from "src/app/purchase/invoice/invoice";
import { InvoiceService } from "src/app/purchase/invoice/invoice-service";
import { SupplierReference } from "../../reference/supplier-reference";
import { AddReferenceToReceptionResult } from "../add-reference-to-reception-result";
import { Reception } from "../reception";
import { ReceptionDetail } from "../reception-detail";
import { ReceptionService } from "../reception-service";

@Component({
  styleUrls: ['./reception-details.component.css'],
  templateUrl: './reception-details.component.html'
})
export class ReceptionDetailsComponent {

  @ViewChild('isbnInput', { static: false }) isbnInput!: ElementRef;

  @ViewChild('modal', { static: false }) modalRef!: ElementRef;

  public error!: any;
  public processing = false;
  public readOnly = false;
  public searching = false;

  public result!: AddReferenceToReceptionResult | null;
  public isbn = "";
  public newIsbn = "";
  public needaConfig!: NeedaConfig;
  public invoice!: Invoice;
  public needaInvoice!: string;
  public purchaseOrders!: number[];
  public reception!: Reception;
  public detailsReceived: ReceptionDetail[] = [];
  public detailsToReceive: ReceptionDetail[] = [];
  public showReceived = false;

  public ordered = 0;
  public toReceive = 0;
  public received = 0;

  public title = "";
  public price = 0;

  public referencesPrintToggle = new Set<string>();

  constructor(
    private invoiceService: InvoiceService,
    private modalService: NgbModal,
    private receptionService: ReceptionService,
    private router: Router,
    activatedRoute: ActivatedRoute,
  ) {
    activatedRoute.paramMap.pipe(
      switchMap(paramMap => this.getReceptionIdFromParams(paramMap)),
      switchMap(receptionId => this.refreshReception(receptionId))
    ).subscribe();
  }

  public confirm() {
    this.error = null;
    this.processing = true;
    this.readOnly = true;

    this.receptionService.confirm(this.reception.id).subscribe({
      next: _ => this.router.navigateByUrl("/inventory/reception"),
      error: _ => {
        this.error = true
        this.processing = false;
        this.readOnly = false;
      },
      complete: () => {
        this.processing = false;
        this.readOnly = false;
      }
    });
  }

  public createReference(modal: NgbModalRef) {
    modal.close();

    this.error = null;
    this.readOnly = true;
    this.searching = true;

    this.receptionService.createReference(this.reception.id, this.newIsbn, this.title, this.price)
      .subscribe({
        next: result => this.handleAddReferenceResult(result),
        error: _ => {
          this.error = true
          this.readOnly = false;
          this.searching = false;

          this.focusIsbnInput();
        },
        complete: () => {
          this.readOnly = false;
          this.searching = false;
        }
      });

  }

  public dismissResult() {
    this.result = null;
  }

  public focusIsbnInput() {
    setTimeout(() => this.isbnInput.nativeElement.focus());
  }

  public searchIsbn() {
    if (this.isbn == "") {
      return;
    }

    var createIsbn = this.result != null && this.result.isbn == this.isbn;

    var isbn = this.isbn;


    this.isbn = "";
    this.result = null;


    if (createIsbn) {
      this.newIsbn = isbn;
      this.title = "";
      this.price = 0;
      this.openNewReferenceModal();

      return;
    }

    this.error = null;
    this.readOnly = true;
    this.searching = true;

    this.receptionService.addReference(this.reception.id, isbn)

      .subscribe({
        next: result => this.handleAddReferenceResult(result),
        error: _ => {
          this.error = true
          this.readOnly = false;
          this.searching = false;

          this.focusIsbnInput();
        },
        complete: () => {
          this.readOnly = false;
          this.searching = false;
        }
      });
  }

  private getReceptionIdFromParams(paramMap: ParamMap): Observable<string> {
    let reception = paramMap.get("reception") || "";
    if (reception == "") {
      this.router.navigateByUrl("/404");

      return EMPTY;
    }

    return of(reception);
  }

  private handleAddReferenceResult(result: AddReferenceToReceptionResult) {
    if (result.reception != null) {
      this.reception = result.reception;
      this.recomputeDetails();

      this.result = null;
    } else {
      this.result = result;
    }

    this.focusIsbnInput();
  }

  public printLabels() {
    var isbns = "";
    for (const isbn of this.referencesPrintToggle) {
      isbns += `isbns=${isbn}&`;
    }
    window.open(`${window.origin}/inventory/reception/${this.reception.id}/labels.pdf?${isbns}`, "_blank");
  }

  public openNewReferenceModal() {
    this.modalService.open(this.modalRef);
  }

  public printLabelsAndConfirm() {
    var isbns = "";
    for (const isbn of this.referencesPrintToggle) {
      isbns += `isbns=${isbn}&`;
    }
    window.open(`${window.origin}/inventory/reception/${this.reception.id}/labels.pdf?${isbns}`, "_blank");
    this.confirm();
  }

  private recomputeDetails() {
    var detailsReceived: ReceptionDetail[] = [];
    var detailsToReceive: ReceptionDetail[] = [];

    this.ordered = 0;
    this.toReceive = 0;
    this.received = 0;
    for (const detail of this.reception.details) {
      this.ordered += detail.orderedQuantity;
      this.toReceive += detail.shippedQuantity;
      this.received += detail.receivedQuantity;

      if (detail.shippedQuantity <= detail.receivedQuantity) {
        detailsReceived.push(detail);
      } else {
        detailsToReceive.push(detail);
      }
    }

    this.detailsReceived = detailsReceived;
    this.detailsToReceive = detailsToReceive;
  }

  public reopen() {
    this.receptionService.reopen(this.reception.id)
      .pipe(tap(reception => this.reception = reception))
      .subscribe();
  }

  private refreshReception(receptionId: string): Observable<Invoice> {
    return this.receptionService.findById(receptionId)
      .pipe(
        tap(reception => this.reception = reception),
        tap(_ => this.recomputeDetails()),
        filter(reception => reception.invoiceId != null),
        switchMap(reception => this.invoiceService.findById(reception.invoiceId)),
        tap(invoice => this.invoice = invoice),
        tap(_ => this.focusIsbnInput())
      );
  }
}